/**
 * ops/ops.ts — словарь атомарных операций движка (tech/21 §7, D14).
 *
 * Каждая операция: (1) предусловия с понятными ошибками → (2) reducer-мутация
 * черновика (applyEvent — единственная точка мутации) → (3) событие в
 * транзиентную FIFO-очередь. Возвраты значений НЕМЕДЛЕННЫ и синхронны
 * (createToken → InstanceId, addEffect → EffectId).
 *
 * Операции НЕ вызывают чужие хуки: реакции идут только через отложенный дренаж
 * очереди (runStep, D14/R1). Этот слой станет фундаментом ctx.ops (M3a) —
 * сигнатуры без церемоний (D5).
 *
 * Счётчики id: операция ЧИТАЕТ nextInstanceSeq/nextEffectSeq (не продвигая),
 * кладёт seq в событие, а продвигает счётчик reducer при применении события —
 * так возврат id синхронен, а мутация остаётся только в reducer'е.
 */
import { applyEvent } from '../events/reducer.js';
import { EventQueue } from '../events/queue.js';
import { interpretOp } from './interpretOp.js';
import { effectiveStats } from '../stats/effectiveStats.js';
import { planShieldMitigation } from '../combat/shields.js';
import { ABSORPTION_READY_ROUND_KEY } from '../combat/attackPermissions.js';
import { requireCell, requireInstance, requirePlayer } from '../state/access.js';
import { compareCellIds, parseCellId } from '../state/board.js';
import { rngInt } from '../rng/rng.js';
import { effectIdOf, instanceIdOf } from '../state/ids.js';
import { assertFiniteNumber, assertInteger, normalizeZero } from '../state/num.js';
export const MELTDOWN_HEAT_THRESHOLD = 4;
/** Игрок, играющий за сторону side (для урона в пустую ячейку, D2). */
function playerBySide(state, side, where) {
    const found = Object.values(state.players).find((p) => p.side === side);
    if (found === undefined) {
        throw new Error(`${where}: не найден игрок стороны '${side}'`);
    }
    return found;
}
/** Разрешить цель операции в цель события (+ ячейка цели, если есть). */
function resolveDealDamageTarget(state, target, where) {
    switch (target.kind) {
        case 'unit': {
            const inst = requireInstance(state, target.instanceId, where);
            if (inst.zone !== 'board' || inst.cellId === undefined) {
                throw new Error(`${where}: юнит "${inst.id}" не на поле (zone: '${inst.zone}') — урон по юниту возможен только на поле`);
            }
            return { target: { kind: 'unit', instanceId: inst.id }, targetCellId: inst.cellId };
        }
        case 'player': {
            requirePlayer(state, target.playerId, where);
            return { target: { kind: 'player', playerId: target.playerId } };
        }
        case 'cell': {
            const cell = requireCell(state, target.cellId, where);
            if (cell.occupantId !== null) {
                return { target: { kind: 'unit', instanceId: cell.occupantId }, targetCellId: cell.id };
            }
            // Пустая ячейка: урон уходит в HP игрока-владельца ячейки (D2).
            const owner = playerBySide(state, cell.side, where);
            return { target: { kind: 'player', playerId: owner.id }, targetCellId: cell.id };
        }
    }
}
function captureReservesCell(state, cellId) {
    return state.captureReservations.some((reservation) => reservation.sourceCellId === cellId);
}
/** Валидация scope эффекта против текущего состояния. */
function validateScope(state, scope, where) {
    switch (scope.kind) {
        case 'table': {
            if (scope.tableId !== 0 && scope.tableId !== 1 && scope.tableId !== 2) {
                throw new Error(`${where}: некорректный tableId ${String(scope.tableId)} (ожидался 0 | 1 | 2)`);
            }
            return;
        }
        case 'lane': {
            if (scope.tableId !== 0 && scope.tableId !== 1 && scope.tableId !== 2) {
                throw new Error(`${where}: некорректный tableId ${String(scope.tableId)} (ожидался 0 | 1 | 2)`);
            }
            if (scope.laneIndex !== 0 && scope.laneIndex !== 1 && scope.laneIndex !== 2) {
                throw new Error(`${where}: некорректный laneIndex ${String(scope.laneIndex)} (ожидался 0 | 1 | 2)`);
            }
            return;
        }
        case 'cell': {
            requireCell(state, scope.cellId, where);
            return;
        }
        case 'player': {
            requirePlayer(state, scope.playerId, where);
            return;
        }
        case 'unit': {
            requireInstance(state, scope.instanceId, where);
            return;
        }
        default: {
            throw new Error(`${where}: неизвестный вид scope ${JSON.stringify(scope)} (ожидался table | lane | cell | player | unit — D19)`);
        }
    }
}
/** Валидация statMods (S2): stat ∈ StatKey (health НЕ слоится — 21 §7.1), op ∈ add|mul|set. */
function validateStatMods(mods, where) {
    if (!Array.isArray(mods)) {
        throw new Error(`${where}: statMods должен быть массивом`);
    }
    return mods.map((mod, i) => {
        if (mod.stat !== 'attack' && mod.stat !== 'maxHealth') {
            throw new Error(`${where}: statMods[${i}].stat "${String(mod.stat)}" не слоится (ожидался attack | maxHealth; health — текущее здоровье, кап = эффективный maxHealth, 21 §7.1)`);
        }
        if (mod.op !== 'add' && mod.op !== 'mul' && mod.op !== 'set') {
            throw new Error(`${where}: statMods[${i}].op "${String(mod.op)}" неизвестен (ожидался add | mul | set)`);
        }
        assertFiniteNumber(mod.value, `${where}: statMods[${i}].value`);
        return { stat: mod.stat, op: mod.op, value: normalizeZero(mod.value) };
    });
}
/** Guard: непустая строка. */
function assertNonEmptyString(x, what, where) {
    if (typeof x !== 'string' || x === '') {
        throw new Error(`${where}: ${what} должен быть непустой строкой (получено ${JSON.stringify(x)})`);
    }
}
/**
 * Связать словарь операций с черновиком состояния и очередью событий.
 * bindOps НЕ копирует состояние: черновик мутируется ЧЕРЕЗ reducer при каждом
 * вызове op. Границу чистоты держат драйверы (runStep/runOp копируют вход).
 *
 * `deps` (M3b, опц.): чистые replacement-хуки для dealDamage/destroy. Без них
 * (M2/low-level) поведение идентично прежнему — урон/смерть без замены.
 */
export function bindOps(draft, queue, deps = {}) {
    const replacementDestroyChain = new Set();
    /** Единый путь: reducer-мутация → событие в очередь (хуки НЕ вызываются). */
    const emit = (event) => {
        // Keep reducer FIFO untouched, but retain the public visual root for every
        // fact emitted by a reactive hook. A death trigger can create a token,
        // draw a card, add a counter, deal damage or change a stat — limiting the
        // edge to StatChanged made every other visible consequence look like an
        // unrelated tail of the combat batch. Redaction remaps/drops the edge
        // together with its parent, so this carries no hidden information.
        const parentIndex = deps.presentationCauseEventIndex?.();
        const annotated = parentIndex === undefined
            ? event
            : { ...event, causedByEventIndex: parentIndex };
        queue.prepare(annotated);
        applyEvent(draft, annotated);
        queue.enqueue(annotated);
    };
    const cleanupBoundToHost = (instanceId) => {
        for (const attachment of [...draft.attachments]
            .filter((candidate) => candidate.hostInstanceId === instanceId)
            .sort((a, b) => a.seq - b.seq)) {
            ops.removeAttachment(attachment.attachmentId, 'host_left');
        }
        const reservation = draft.captureReservations.find((candidate) => candidate.targetInstanceId === instanceId);
        if (reservation !== undefined)
            ops.returnCapture(reservation.captureId, 'left');
    };
    const ops = {
        dealDamage(target, amount, sourceId) {
            const where = 'ops.dealDamage';
            assertFiniteNumber(amount, where);
            if (amount < 0) {
                throw new Error(`${where}: урон не может быть отрицательным (получено ${amount})`);
            }
            if (sourceId !== undefined) {
                // Существование НЕ проверяем: источник мог уже уйти с поля (например,
                // посмертный урон токена, уже удалённого из instances по D25).
                assertNonEmptyString(sourceId, 'sourceId', where);
            }
            const resolved = resolveDealDamageTarget(draft, target, where);
            // Фиксированное округление вниз + нормализация -0 (tech/23 §5; ревью M1).
            const base = normalizeZero(Math.floor(amount));
            const planned = {
                ...(sourceId !== undefined ? { sourceId } : {}),
                origin: 'effect',
                target: resolved.target,
                ...(resolved.targetCellId !== undefined ? { targetCellId: resolved.targetCellId } : {}),
                base,
                amount: base,
            };
            // Цепочка чистых модификаторов/замены (R3/D16, tech/23 §5): редирект/изменение/
            // отмена. Без deps — идентичность. null ⇒ урон предотвращён (события нет).
            const deterministic = deps.applyDamageReplacements
                ? deps.applyDamageReplacements(draft, planned)
                : planned;
            if (deterministic === null) {
                return;
            }
            const randomized = deps.applyRandomDamageReplacement
                ? deps.applyRandomDamageReplacement(draft, deterministic)
                : { instruction: deterministic };
            if (randomized.event !== undefined)
                emit(randomized.event);
            const final = randomized.instruction;
            if (final === null)
                return;
            const interception = deps.applyIncomingDamageInterceptors?.(draft, ops, final)
                ?? { instruction: final, transfers: [] };
            const intercepted = interception.instruction;
            const shielded = planShieldMitigation(draft, [intercepted])[0];
            for (const consumption of shielded.consumptions) {
                emit({
                    type: 'ShieldAbsorbed', effectId: consumption.effectId,
                    sourceInstanceId: consumption.sourceInstanceId,
                    targetInstanceId: consumption.targetInstanceId,
                    amount: consumption.amount, remaining: consumption.remaining,
                });
                if (consumption.remaining === 0) {
                    emit({
                        type: 'ShieldDepleted', effectId: consumption.effectId,
                        sourceInstanceId: consumption.sourceInstanceId,
                        targetInstanceId: consumption.targetInstanceId,
                    });
                    if (consumption.onDepletedCool !== undefined) {
                        ops.cool(consumption.targetInstanceId, consumption.onDepletedCool);
                    }
                    ops.removeEffect(consumption.effectId);
                }
            }
            // Клампинг — последний шаг после source-bound Щитов.
            const finalAmount = normalizeZero(Math.max(0, Math.floor(shielded.instruction.amount)));
            let lethal = false;
            let targetDefId;
            let targetControllerId;
            if (intercepted.target.kind === 'unit') {
                const target = requireInstance(draft, intercepted.target.instanceId, where);
                targetDefId = target.defId;
                targetControllerId = target.controllerId;
                const before = effectiveStats(draft, intercepted.target.instanceId).health;
                lethal = before > 0 && normalizeZero(before - finalAmount) <= 0;
            }
            emit({
                type: 'DamageDealt',
                ...(intercepted.sourceId !== undefined ? { sourceId: intercepted.sourceId } : {}),
                origin: intercepted.origin,
                target: intercepted.target,
                ...(intercepted.targetCellId !== undefined ? { targetCellId: intercepted.targetCellId } : {}),
                ...(targetDefId !== undefined ? { targetDefId } : {}),
                ...(targetControllerId !== undefined ? { targetControllerId } : {}),
                ...(planned.base !== finalAmount + shielded.absorbed ? { baseAmount: planned.base } : {}),
                amount: finalAmount,
                ...(shielded.absorbed > 0 ? { absorbed: shielded.absorbed } : {}),
                ...(lethal ? { lethal: true } : {}),
            });
            // Переносы перехваченного урона («телохранитель»): обычный effect-урон от
            // носителя перехватчика сразу после текущей инструкции. Исчезнувший к этому
            // моменту получатель детерминированно пропускается.
            for (const transfer of interception.transfers) {
                const receiver = draft.instances[transfer.instanceId];
                if (receiver === undefined || receiver.zone !== 'board' || receiver.cellId === undefined
                    || draft.board.cells[receiver.cellId]?.occupantId !== receiver.id)
                    continue;
                ops.dealDamage({ kind: 'unit', instanceId: transfer.instanceId }, transfer.amount, transfer.sourceId);
            }
        },
        moveUnit(instanceId, toCellId) {
            const where = 'ops.moveUnit';
            const inst = requireInstance(draft, instanceId, where);
            if (inst.zone !== 'board' || inst.cellId === undefined) {
                throw new Error(`${where}: юнит "${instanceId}" не на поле (zone: '${inst.zone}')`);
            }
            const to = requireCell(draft, toCellId, where);
            if (toCellId === inst.cellId) {
                throw new Error(`${where}: юнит "${instanceId}" уже в ячейке "${toCellId}"`);
            }
            if (to.occupantId !== null || captureReservesCell(draft, toCellId) || deps.isCellReserved?.(toCellId) === true) {
                throw new Error(`${where}: ячейка "${toCellId}" занята ("${to.occupantId}")`);
            }
            emit({ type: 'UnitMoved', instanceId, from: inst.cellId, to: toCellId });
        },
        drawCard(playerId, n, sourceId) {
            const where = 'ops.drawCard';
            const player = requirePlayer(draft, playerId, where);
            assertInteger(n, where);
            if (n < 0) {
                throw new Error(`${where}: n не может быть отрицательным (получено ${n})`);
            }
            if (sourceId !== undefined)
                assertNonEmptyString(sourceId, 'sourceId', where);
            const source = sourceId === undefined ? undefined : draft.instances[sourceId];
            const sourceDefinitionIsPublic = source !== undefined
                && (source.ownerId === playerId || ['board', 'discard', 'attachment'].includes(source.zone));
            for (let k = 0; k < n; k += 1) {
                const top = player.deck[0];
                if (top === undefined) {
                    // Усталость — следствие самого запроса добора, а не урон карты,
                    // которая его вызвала. Поэтому sourceId намеренно не передаётся.
                    ops.dealDamage({ kind: 'player', playerId }, 1);
                    continue;
                }
                emit({
                    type: 'CardDrawn', playerId, instanceId: top,
                    ...(sourceId !== undefined ? { sourceId } : {}),
                    ...(sourceDefinitionIsPublic ? { sourceDefId: source.defId } : {}),
                });
                emit({ type: 'CardMoved', instanceId: top, from: 'deck', to: 'hand' });
            }
        },
        restorePlayerHealth(playerId, amount, sourceId) {
            const where = 'ops.restorePlayerHealth';
            const player = requirePlayer(draft, playerId, where);
            assertInteger(amount, where);
            if (amount < 0)
                throw new Error(`${where}: amount не может быть отрицательным (получено ${amount})`);
            if (sourceId !== undefined)
                assertNonEmptyString(sourceId, 'sourceId', where);
            if (amount === 0)
                return;
            const current = player.health + amount;
            emit({
                type: 'PlayerHealthRestored',
                playerId,
                previous: player.health,
                current,
                amount,
                ...(sourceId !== undefined ? { sourceId } : {}),
            });
        },
        createToken(defId, ownerId, cellId, stats, vars) {
            const where = 'ops.createToken';
            assertNonEmptyString(defId, 'defId', where);
            requirePlayer(draft, ownerId, where);
            if (cellId !== undefined) {
                const cell = requireCell(draft, cellId, where);
                if (cell.occupantId !== null || captureReservesCell(draft, cellId) || deps.isCellReserved?.(cellId) === true) {
                    throw new Error(`${where}: ячейка "${cellId}" занята ("${cell.occupantId}")`);
                }
            }
            const registryStats = stats === undefined ? deps.tokenBaseStats?.(defId) : undefined;
            const attack = stats?.attack ?? registryStats?.attack ?? 0;
            const health = stats?.health ?? registryStats?.health ?? 1;
            const maxHealth = stats?.maxHealth ?? health;
            assertInteger(attack, `${where}: stats.attack`);
            assertInteger(health, `${where}: stats.health`);
            assertInteger(maxHealth, `${where}: stats.maxHealth`);
            // Читаем счётчик, НЕ продвигая: продвинет reducer при применении события.
            const seq = draft.nextInstanceSeq;
            const instanceId = instanceIdOf(seq);
            emit({
                type: 'TokenCreated',
                instanceId,
                ...(cellId !== undefined ? { cellId } : {}),
                seq,
                defId,
                ownerId,
                stats: {
                    attack: normalizeZero(attack),
                    health: normalizeZero(health),
                    maxHealth: normalizeZero(maxHealth),
                },
                vars: vars !== undefined ? structuredClone(vars) : {},
            });
            if (cellId !== undefined) {
                emit({ type: 'UnitEnteredPlay', instanceId, cellId, cause: 'token' });
            }
            return instanceId;
        },
        tryCreateToken(defId, ownerId, cellId, stats, vars) {
            const where = 'ops.tryCreateToken';
            assertNonEmptyString(defId, 'defId', where);
            requirePlayer(draft, ownerId, where);
            const cell = requireCell(draft, cellId, where);
            if (cell.occupantId !== null || captureReservesCell(draft, cellId) || deps.isCellReserved?.(cellId) === true) {
                emit({
                    type: 'TokenCreationSkipped', defId, ownerId, cellId,
                    tableId: cell.tableId,
                    reason: cell.occupantId !== null ? 'occupied' : 'reserved',
                });
                return null;
            }
            return ops.createToken(defId, ownerId, cellId, stats, vars);
        },
        createTokenInLocation(defId, ownerId, tableId, stats, vars) {
            const where = 'ops.createTokenInLocation';
            assertNonEmptyString(defId, 'defId', where);
            const owner = requirePlayer(draft, ownerId, where);
            if (tableId !== 0 && tableId !== 1 && tableId !== 2) {
                throw new Error(`${where}: tableId должен быть 0 | 1 | 2`);
            }
            const candidate = Object.values(draft.board.cells)
                .filter((cell) => cell.tableId === tableId && cell.side === owner.side)
                .sort((a, b) => a.laneIndex - b.laneIndex)
                .find((cell) => cell.occupantId === null && !captureReservesCell(draft, cell.id) && deps.isCellReserved?.(cell.id) !== true);
            if (candidate === undefined) {
                emit({ type: 'TokenCreationSkipped', defId, ownerId, tableId, reason: 'location_full' });
                return null;
            }
            return ops.tryCreateToken(defId, ownerId, candidate.id, stats, vars);
        },
        createCardInHand(defId, ownerId) {
            const where = 'ops.createCardInHand';
            assertNonEmptyString(defId, 'defId', where);
            requirePlayer(draft, ownerId, where);
            const base = deps.tokenBaseStats?.(defId);
            if (base === undefined)
                throw new Error(`${where}: неизвестный defId "${defId}"`);
            const seq = draft.nextInstanceSeq;
            const instanceId = instanceIdOf(seq);
            emit({
                type: 'CardCreated', instanceId, seq, defId, ownerId,
                stats: { attack: base.attack, health: base.health, maxHealth: base.health },
            });
            return instanceId;
        },
        consumeCard(instanceId) {
            const where = 'ops.consumeCard';
            const inst = requireInstance(draft, instanceId, where);
            if (inst.zone !== 'board' || inst.cellId === undefined) {
                throw new Error(`${where}: consume доступен только полевой карте`);
            }
            cleanupBoundToHost(inst.id);
            const address = parseCellId(inst.cellId);
            emit({
                type: 'CardConsumed', instanceId: inst.id, defId: inst.defId,
                ownerId: inst.ownerId, cellId: inst.cellId, tableId: address.tableId,
                isToken: inst.isToken === true,
            });
        },
        absorbCards(receiverId, donorIds) {
            const where = 'ops.absorbCards';
            const receiver = requireInstance(draft, receiverId, where);
            const receiverOnBoard = receiver.zone === 'board'
                && receiver.cellId !== undefined
                && draft.board.cells[receiver.cellId]?.occupantId === receiver.id;
            const receiverIsCurrentReveal = receiver.zone === 'hand'
                && receiver.cellId === undefined
                && deps.isPendingFieldReceiver?.(receiver.id) === true;
            if (!receiverOnBoard && !receiverIsCurrentReveal) {
                throw new Error(`${where}: получатель должен занимать ячейку поля или быть текущей раскрытой FIELD-картой`);
            }
            if (!Array.isArray(donorIds))
                throw new Error(`${where}: donorIds должен быть массивом`);
            if (donorIds.length === 0)
                return;
            if (new Set(donorIds).size !== donorIds.length) {
                throw new Error(`${where}: доноры должны быть различными`);
            }
            if (donorIds.includes(receiverId)) {
                throw new Error(`${where}: получатель не может быть донором`);
            }
            // Полный snapshot и все предусловия проверяются до первой мутации.
            const donors = donorIds.map((donorId) => {
                const donor = requireInstance(draft, donorId, where);
                if (donor.zone !== 'board' || donor.cellId === undefined
                    || draft.board.cells[donor.cellId]?.occupantId !== donor.id) {
                    throw new Error(`${where}: донор "${donorId}" должен занимать ячейку поля`);
                }
                return {
                    instanceId: donor.id,
                    defId: donor.defId,
                    ownerId: donor.ownerId,
                    controllerId: donor.controllerId,
                    cellId: donor.cellId,
                    isToken: donor.isToken === true,
                    seq: donor.seq,
                    stats: structuredClone(donor.stats),
                };
            }).sort((a, b) => compareCellIds(a.cellId, b.cellId) || a.seq - b.seq);
            const totalAttack = donors.reduce((sum, donor) => sum + donor.stats.attack, 0);
            const totalHealth = donors.reduce((sum, donor) => sum + Math.max(0, donor.stats.health), 0);
            if (!Number.isSafeInteger(totalAttack) || !Number.isSafeInteger(totalHealth)) {
                throw new Error(`${where}: сумма передаваемых статов вышла за safe integer`);
            }
            const receiverBefore = structuredClone(receiver.stats);
            const donorSet = new Set(donors.map((donor) => donor.instanceId));
            // Unit-scoped effects cannot retain a carrier that absorption removes;
            // ordinary source-bound effects keep the generic persistent lifecycle.
            const effectsToRemove = draft.effects
                .filter((effect) => (effect.scope.kind === 'unit' && donorSet.has(effect.scope.instanceId))
                || (effect.sourceId !== undefined && donorSet.has(effect.sourceId) && effect.persistent !== true))
                .sort((a, b) => a.seq - b.seq)
                .map((effect) => effect.id);
            for (const effectId of effectsToRemove)
                ops.removeEffect(effectId);
            for (const donor of donors)
                ops.consumeCard(donor.instanceId);
            ops.modifyStat(receiverId, 'attack', totalAttack);
            ops.modifyStat(receiverId, 'maxHealth', totalHealth);
            ops.modifyStat(receiverId, 'health', totalHealth);
            // Поглощение завершает формирование получателя: если он появился в этом
            // раунде, общий attack-eligibility разрешает ему атаковать сразу. Раунд в
            // var не превращает карту в постоянный Натиск и безопасен для hydration.
            ops.setInstanceVar(receiverId, ABSORPTION_READY_ROUND_KEY, draft.round);
            const receiverAfter = requireInstance(draft, receiverId, where);
            emit({
                type: 'CardsAbsorbed',
                receiver: {
                    instanceId: receiver.id,
                    defId: receiver.defId,
                    ownerId: receiver.ownerId,
                    controllerId: receiver.controllerId,
                    statsBefore: receiverBefore,
                    statsAfter: structuredClone(receiverAfter.stats),
                },
                donors: donors.map(({ seq: ignoredSeq, ...donor }) => {
                    void ignoredSeq;
                    return donor;
                }),
                totals: { attack: totalAttack, health: totalHealth },
            });
        },
        transformCard(instanceId, toDefId, stats, options) {
            const where = 'ops.transformCard';
            const inst = requireInstance(draft, instanceId, where);
            if (inst.zone !== 'board' || inst.cellId === undefined) {
                throw new Error(`${where}: transform доступен только полевой карте`);
            }
            assertNonEmptyString(toDefId, 'toDefId', where);
            const registryStats = stats === undefined ? deps.tokenBaseStats?.(toDefId) : undefined;
            if (stats === undefined && registryStats === undefined) {
                throw new Error(`${where}: неизвестный defId "${toDefId}"`);
            }
            const attack = stats?.attack ?? registryStats.attack;
            const health = stats?.health ?? registryStats.health;
            const maxHealth = stats?.maxHealth ?? health;
            assertInteger(attack, `${where}: stats.attack`);
            assertInteger(health, `${where}: stats.health`);
            assertInteger(maxHealth, `${where}: stats.maxHealth`);
            let enteredRound = draft.round;
            if (options?.enteredRound !== undefined) {
                assertInteger(options.enteredRound, `${where}: options.enteredRound`);
                if (options.enteredRound < 0 || options.enteredRound > draft.round) {
                    throw new Error(`${where}: options.enteredRound должен быть в диапазоне 0..${draft.round}`);
                }
                enteredRound = options.enteredRound;
            }
            emit({
                type: 'CardTransformed', instanceId, fromDefId: inst.defId, toDefId,
                stats: { attack, health, maxHealth }, enteredRound,
            });
        },
        completeAssembly(ownerId, fragmentIds, robotDefId, stats) {
            const where = 'ops.completeAssembly';
            requirePlayer(draft, ownerId, where);
            if (fragmentIds.length !== 2 || fragmentIds[0] === fragmentIds[1]) {
                throw new Error(`${where}: нужны два разных fragment instanceId`);
            }
            const fragments = fragmentIds.map((id) => requireInstance(draft, id, where));
            for (const fragment of fragments) {
                if (fragment.controllerId !== ownerId || fragment.zone !== 'board' || fragment.cellId === undefined) {
                    throw new Error(`${where}: оба фрагмента должны быть полевыми картами владельца`);
                }
            }
            const ordered = fragments.sort((a, b) => compareCellIds(a.cellId, b.cellId) || a.seq - b.seq);
            const firstCell = ordered[0].cellId;
            const secondCell = ordered[1].cellId;
            const tableId = parseCellId(firstCell).tableId;
            if (parseCellId(secondCell).tableId !== tableId) {
                throw new Error(`${where}: Сборка требует два фрагмента одной локации`);
            }
            const ids = [ordered[0].id, ordered[1].id];
            const cells = [firstCell, secondCell];
            ops.consumeCard(ids[0]);
            ops.consumeCard(ids[1]);
            const robotInstanceId = ops.createToken(robotDefId, ownerId, firstCell, stats);
            emit({
                type: 'AssemblyCompleted', ownerId, fragmentIds: ids, fragmentCellIds: cells,
                tableId, robotInstanceId, robotCellId: firstCell,
            });
            return robotInstanceId;
        },
        grantTemporaryEnergy(playerId, amount) {
            const where = 'ops.grantTemporaryEnergy';
            requirePlayer(draft, playerId, where);
            assertInteger(amount, where);
            if (amount < 1)
                throw new Error(`${where}: amount должен быть ≥ 1`);
            emit({ type: 'TemporaryEnergyGranted', playerId, amount, availableRound: draft.round + 1 });
        },
        applyHack(victimInstanceId, initiatorPlayerId, sourceInstanceId) {
            const where = 'ops.applyHack';
            const victim = requireInstance(draft, victimInstanceId, where);
            if (victim.zone !== 'board' || victim.cellId === undefined) {
                throw new Error(`${where}: жертва должна находиться на поле`);
            }
            requirePlayer(draft, initiatorPlayerId, where);
            if (sourceInstanceId !== undefined)
                assertNonEmptyString(sourceInstanceId, 'sourceInstanceId', where);
            if (victim.hack !== undefined)
                ops.removeHack(victim.id, 'replaced');
            const seq = draft.nextHackSeq;
            const hackId = `h${seq}`;
            emit({
                type: 'HackApplied',
                marker: {
                    hackId, seq, victimInstanceId: victim.id,
                    victimControllerIdAtApply: victim.controllerId,
                    initiatorPlayerId,
                    ...(sourceInstanceId !== undefined ? { sourceInstanceId } : {}),
                },
            });
            return hackId;
        },
        removeHack(victimInstanceId, reason = 'manual') {
            const victim = requireInstance(draft, victimInstanceId, 'ops.removeHack');
            if (victim.hack === undefined)
                return;
            emit({ type: 'HackRemoved', hackId: victim.hack.hackId, victimInstanceId, reason });
        },
        attachCard(sourceCardInstanceId, hostInstanceId, vars) {
            const where = 'ops.attachCard';
            const source = requireInstance(draft, sourceCardInstanceId, where);
            const host = requireInstance(draft, hostInstanceId, where);
            if (source.zone !== 'hand')
                throw new Error(`${where}: исходная карта должна быть в руке`);
            if (host.zone !== 'board' || host.cellId === undefined)
                throw new Error(`${where}: носитель должен быть на поле`);
            const seq = draft.nextAttachmentSeq;
            const attachmentId = `a${seq}`;
            emit({ type: 'CardMoved', instanceId: source.id, from: 'hand', to: 'attachment' });
            emit({
                type: 'AttachmentAdded',
                attachment: {
                    attachmentId, seq, defId: source.defId, ownerId: source.ownerId,
                    sourceCardInstanceId: source.id, hostInstanceId: host.id,
                    lifecycle: 'active', vars: structuredClone(vars ?? {}),
                },
            });
            return attachmentId;
        },
        removeAttachment(attachmentId, reason = 'manual') {
            const attachment = draft.attachments.find((candidate) => candidate.attachmentId === attachmentId);
            if (attachment === undefined)
                return;
            emit({
                type: 'AttachmentRemoved', attachmentId,
                sourceCardInstanceId: attachment.sourceCardInstanceId,
                ownerId: attachment.ownerId, reason,
            });
            const source = draft.instances[attachment.sourceCardInstanceId];
            if (source?.zone === 'attachment') {
                emit({ type: 'CardMoved', instanceId: source.id, from: 'attachment', to: 'discard' });
            }
        },
        triggerAttachment(attachmentId, eventType) {
            const attachment = draft.attachments.find((candidate) => candidate.attachmentId === attachmentId);
            if (attachment === undefined)
                throw new Error(`ops.triggerAttachment: attachment не найден`);
            assertNonEmptyString(eventType, 'eventType', 'ops.triggerAttachment');
            emit({ type: 'AttachmentTriggered', attachmentId, hostInstanceId: attachment.hostInstanceId, eventType });
        },
        setAttachmentVar(attachmentId, key, value) {
            if (!draft.attachments.some((candidate) => candidate.attachmentId === attachmentId)) {
                throw new Error(`ops.setAttachmentVar: attachment не найден`);
            }
            ops.setVar({ kind: 'attachment', attachmentId }, key, value);
        },
        captureUnit(instanceId, newControllerId, destinationCellId, options) {
            const where = 'ops.captureUnit';
            const instance = draft.instances[instanceId];
            const destination = draft.board.cells[destinationCellId];
            const controller = draft.players[newControllerId];
            const fizzle = (reason) => {
                emit({ type: 'CaptureFizzled', instanceId, newControllerId, destinationCellId, reason });
                return null;
            };
            if (instance === undefined || instance.zone !== 'board' || instance.cellId === undefined || controller === undefined) {
                return fizzle('invalid_target');
            }
            if (instance.controllerId !== instance.ownerId || draft.captureReservations.some((r) => r.targetInstanceId === instance.id)) {
                return fizzle('already_captured');
            }
            const source = draft.board.cells[instance.cellId];
            if (destination === undefined || destination.side !== controller.side || destination.occupantId !== null
                || captureReservesCell(draft, destinationCellId) || deps.isCellReserved?.(destinationCellId) === true) {
                return fizzle('destination_unavailable');
            }
            if (source?.tableId !== destination.tableId)
                return fizzle('wrong_location');
            const returnFreeze = options.returnFreeze ?? 0;
            assertInteger(returnFreeze, `${where}: returnFreeze`);
            if (returnFreeze < 0)
                throw new Error(`${where}: returnFreeze должен быть >= 0`);
            if (instance.hack !== undefined)
                ops.removeHack(instance.id, 'controller_changed');
            const seq = draft.nextCaptureSeq;
            const captureId = `c${seq}`;
            const reservation = options.temporary ? {
                captureId, seq, targetInstanceId: instance.id,
                sourceCellId: instance.cellId, destinationCellId,
                previousControllerId: instance.controllerId, newControllerId,
                expiresRound: draft.round, returnFreeze,
            } : undefined;
            emit({
                type: 'CaptureApplied', captureId, seq, instanceId: instance.id,
                fromCellId: instance.cellId, toCellId: destinationCellId,
                previousControllerId: instance.controllerId, newControllerId,
                temporary: options.temporary,
                ...(options.ignoreSummoningDelay === true ? { ignoreSummoningDelay: true } : {}),
                ...(reservation !== undefined ? { reservation } : {}),
            });
            return captureId;
        },
        returnCapture(captureId, reason = 'expired') {
            const reservation = draft.captureReservations.find((candidate) => candidate.captureId === captureId);
            if (reservation === undefined)
                return;
            const instance = draft.instances[reservation.targetInstanceId];
            const alive = instance !== undefined && instance.zone === 'board' && instance.cellId !== undefined;
            emit({
                type: 'CaptureReturned', captureId, instanceId: reservation.targetInstanceId,
                ...(alive ? { fromCellId: instance.cellId } : {}),
                toCellId: reservation.sourceCellId,
                previousControllerId: reservation.newControllerId,
                restoredControllerId: reservation.previousControllerId,
                returnFreeze: reservation.returnFreeze,
                reason: alive && reason === 'expired' ? 'expired' : 'left',
            });
            if (alive && reason === 'expired' && reservation.returnFreeze > 0) {
                ops.freeze(instance.id, reservation.returnFreeze);
            }
        },
        queueExtraAttack(attackerId, sourceCardInstanceId) {
            const attacker = requireInstance(draft, attackerId, 'ops.queueExtraAttack');
            if (attacker.zone !== 'board' || attacker.cellId === undefined)
                throw new Error('ops.queueExtraAttack: атакующий не на поле');
            if (sourceCardInstanceId !== undefined)
                assertNonEmptyString(sourceCardInstanceId, 'sourceCardInstanceId', 'ops.queueExtraAttack');
            const seq = draft.nextExtraAttackSeq;
            const id = `x${seq}`;
            emit({ type: 'ExtraAttackQueued', request: { id, seq, attackerId, ...(sourceCardInstanceId !== undefined ? { sourceCardInstanceId } : {}) } });
            return id;
        },
        scheduleTimedDestroy(instanceIds, deadlineRound) {
            const where = 'ops.scheduleTimedDestroy';
            assertInteger(deadlineRound, `${where}: deadlineRound`);
            if (deadlineRound <= draft.round)
                throw new Error(`${where}: deadlineRound должен быть будущим раундом`);
            const unique = [...new Set(instanceIds)];
            if (unique.length === 0 || unique.length !== instanceIds.length) {
                throw new Error(`${where}: instanceIds должны быть непустыми и уникальными`);
            }
            const ordered = unique
                .map((id) => requireInstance(draft, id, where))
                .sort((a, b) => a.seq - b.seq)
                .map((instance) => instance.id);
            const seq = draft.nextTimedDestroySeq;
            const id = `td${seq}`;
            emit({ type: 'TimedDestroyScheduled', marker: { id, seq, instanceIds: ordered, deadlineRound } });
            return id;
        },
        resolveTimedDestroy(markerId) {
            const where = 'ops.resolveTimedDestroy';
            const marker = draft.timedDestroys.find((candidate) => candidate.id === markerId);
            if (marker === undefined)
                throw new Error(`${where}: marker "${markerId}" не существует`);
            if (draft.round < marker.deadlineRound)
                return;
            const surviving = marker.instanceIds
                .map((id) => draft.instances[id])
                .filter((instance) => instance !== undefined && instance.zone === 'board' && instance.cellId !== undefined)
                .sort((a, b) => a.seq - b.seq)[0];
            emit({
                type: 'TimedDestroyResolved', markerId,
                ...(surviving !== undefined ? { instanceId: surviving.id, cellId: surviving.cellId } : {}),
                deadlineRound: marker.deadlineRound, resolvedRound: draft.round,
            });
            if (surviving !== undefined) {
                cleanupBoundToHost(surviving.id);
                const cellId = surviving.cellId;
                emit({
                    type: 'UnitDied', instanceId: surviving.id, cellId, defId: surviving.defId,
                    ownerId: surviving.ownerId, controllerIdAtDeath: surviving.controllerId,
                    isToken: surviving.isToken === true, cause: 'timed',
                });
                if (surviving.isToken !== true) {
                    emit({ type: 'CardMoved', instanceId: surviving.id, from: 'board', to: 'discard' });
                }
            }
        },
        setInstanceVar(instanceId, key, value) {
            requireInstance(draft, instanceId, 'ops.setInstanceVar');
            ops.setVar({ kind: 'unit', instanceId }, key, value);
        },
        modifyStat(instanceId, stat, delta, sourceId) {
            const where = 'ops.modifyStat';
            const inst = requireInstance(draft, instanceId, where);
            if (stat !== 'attack' && stat !== 'health' && stat !== 'maxHealth') {
                throw new Error(`${where}: неизвестный стат "${String(stat)}" (ожидался attack | health | maxHealth)`);
            }
            assertFiniteNumber(delta, where);
            assertInteger(delta, where);
            if (sourceId !== undefined)
                assertNonEmptyString(sourceId, 'sourceId', where);
            const attribution = sourceId !== undefined ? { sourceId } : {};
            if (stat === 'health') {
                // Кап лечения — ЭФФЕКТИВНЫЙ maxHealth (21 §7.1). Здоровье выше капа
                // (например, после снятия +maxHealth-эффекта) лечением не срезается.
                const before = inst.stats.health;
                const cap = Math.max(before, effectiveStats(draft, instanceId).maxHealth);
                const after = delta > 0 ? Math.min(before + delta, cap) : before + delta;
                emit({ type: 'StatChanged', instanceId, stat, delta: normalizeZero(after - before), ...attribution });
                return;
            }
            emit({ type: 'StatChanged', instanceId, stat, delta: normalizeZero(delta), ...attribution });
            if (stat === 'maxHealth') {
                // Кап текущего health по НОВОМУ эффективному maxHealth — вторым
                // фактическим событием (журнал остаётся полным и буквальным).
                const capped = effectiveStats(draft, instanceId).maxHealth;
                if (inst.stats.health > capped) {
                    emit({
                        type: 'StatChanged',
                        instanceId,
                        stat: 'health',
                        delta: normalizeZero(capped - inst.stats.health),
                    });
                }
            }
        },
        restoreUnitHealth(instanceId, amount, sourceId) {
            const where = 'ops.restoreUnitHealth';
            const inst = requireInstance(draft, instanceId, where);
            assertInteger(amount, where);
            if (amount < 0)
                throw new Error(`${where}: amount должен быть ≥ 0`);
            if (sourceId !== undefined)
                assertNonEmptyString(sourceId, 'sourceId', where);
            if (amount === 0)
                return;
            // Кап лечения — ЭФФЕКТИВНЫЙ maxHealth (21 §7.1); здоровье выше капа не срезаем.
            const before = inst.stats.health;
            const cap = Math.max(before, effectiveStats(draft, instanceId).maxHealth);
            const after = Math.min(before + amount, cap);
            const applied = after - before;
            if (applied <= 0)
                return; // фактического восстановления нет — событие не эмитим
            emit({
                type: 'UnitHealed', instanceId, previous: before, current: after, amount: applied,
                ...(sourceId !== undefined ? { sourceId } : {}),
            });
        },
        addHeat(instanceId, amount) {
            const where = 'ops.addHeat';
            const inst = requireInstance(draft, instanceId, where);
            assertInteger(amount, where);
            if (amount < 0)
                throw new Error(`${where}: amount должен быть ≥ 0`);
            if (amount === 0)
                return;
            const previous = inst.heat ?? 0;
            const current = previous + amount;
            emit({ type: 'HeatChanged', instanceId, previous, current, reason: 'heated' });
            const live = draft.instances[instanceId];
            if (current >= MELTDOWN_HEAT_THRESHOLD && live?.zone === 'board' && live.cellId !== undefined) {
                ops.destroy(instanceId);
            }
        },
        cool(instanceId, amount) {
            const where = 'ops.cool';
            const inst = requireInstance(draft, instanceId, where);
            assertInteger(amount, where);
            if (amount < 0)
                throw new Error(`${where}: amount должен быть ≥ 0`);
            if (deps.isCoolingPrevented?.(inst.defId) === true)
                return false;
            const previous = inst.heat ?? 0;
            const current = Math.max(0, previous - amount);
            if (current === previous)
                return false;
            emit({ type: 'HeatChanged', instanceId, previous, current, reason: 'cooled' });
            return true;
        },
        freeze(instanceId, amount) {
            const where = 'ops.freeze';
            const inst = requireInstance(draft, instanceId, where);
            assertInteger(amount, where);
            if (amount < 0)
                throw new Error(`${where}: amount должен быть ≥ 0`);
            if (amount === 0)
                return;
            const previous = inst.freeze ?? 0;
            emit({ type: 'FreezeChanged', instanceId, previous, current: previous + amount, reason: 'frozen' });
        },
        thaw(instanceId, amount) {
            const where = 'ops.thaw';
            const inst = requireInstance(draft, instanceId, where);
            assertInteger(amount, where);
            if (amount < 0)
                throw new Error(`${where}: amount должен быть ≥ 0`);
            const previous = inst.freeze ?? 0;
            const current = Math.max(0, previous - amount);
            if (current === previous)
                return;
            emit({ type: 'FreezeChanged', instanceId, previous, current, reason: 'thawed' });
        },
        addShield(defId, instanceId, amount, sourceInstanceId, onDepletedCool) {
            const where = 'ops.addShield';
            requireInstance(draft, instanceId, where);
            assertNonEmptyString(defId, 'defId', where);
            assertNonEmptyString(sourceInstanceId, 'sourceInstanceId', where);
            assertInteger(amount, where);
            if (amount < 1)
                throw new Error(`${where}: amount должен быть ≥ 1`);
            if (onDepletedCool !== undefined) {
                assertInteger(onDepletedCool, where);
                if (onDepletedCool < 1)
                    throw new Error(`${where}: onDepletedCool должен быть ≥ 1`);
            }
            return ops.addEffect({
                defId,
                scope: { kind: 'unit', instanceId },
                sourceId: sourceInstanceId,
                shield: { sourceInstanceId, remaining: amount, ...(onDepletedCool !== undefined ? { onDepletedCool } : {}) },
            });
        },
        addEffect(input) {
            const where = 'ops.addEffect';
            assertNonEmptyString(input.defId, 'defId', where);
            validateScope(draft, input.scope, where);
            if (input.sourceId !== undefined) {
                assertNonEmptyString(input.sourceId, 'sourceId', where);
            }
            if (input.duration !== undefined) {
                assertInteger(input.duration, `${where}: duration`);
                if (input.duration < 1) {
                    throw new Error(`${where}: duration должен быть ≥ 1 раунда (получено ${input.duration}); бессрочный эффект — без duration (D21)`);
                }
            }
            if (input.persistent !== undefined && typeof input.persistent !== 'boolean') {
                throw new Error(`${where}: persistent должен быть boolean (получено ${String(input.persistent)})`);
            }
            const statMods = input.statMods !== undefined ? validateStatMods(input.statMods, where) : undefined;
            if (input.shield !== undefined) {
                assertNonEmptyString(input.shield.sourceInstanceId, 'shield.sourceInstanceId', where);
                assertInteger(input.shield.remaining, `${where}: shield.remaining`);
                if (input.shield.remaining < 1)
                    throw new Error(`${where}: shield.remaining должен быть ≥ 1`);
            }
            // Читаем счётчик, НЕ продвигая: продвинет reducer при применении события.
            const seq = draft.nextEffectSeq;
            const effectId = effectIdOf(seq);
            // Канонический порядок ключей Effect — как в tech/21 §2 (байт-стабильность).
            const effect = {
                id: effectId,
                seq,
                defId: input.defId,
                scope: structuredClone(input.scope),
                ...(input.sourceId !== undefined ? { sourceId: input.sourceId } : {}),
                ...(input.duration !== undefined ? { duration: input.duration } : {}),
                ...(input.persistent !== undefined ? { persistent: input.persistent } : {}),
                ...(statMods !== undefined ? { statMods } : {}),
                ...(input.shield !== undefined ? { shield: structuredClone(input.shield) } : {}),
                vars: input.vars !== undefined ? structuredClone(input.vars) : {},
            };
            emit({ type: 'EffectAdded', effectId, effect });
            return effectId;
        },
        removeEffect(effectId) {
            const where = 'ops.removeEffect';
            if (!draft.effects.some((e) => e.id === effectId)) {
                throw new Error(`${where}: неизвестный эффект "${effectId}"`);
            }
            emit({ type: 'EffectRemoved', effectId });
        },
        modifyEffectCounter(effectId, key, delta, reason) {
            const where = 'ops.modifyEffectCounter';
            assertNonEmptyString(key, 'key', where);
            if (!Number.isSafeInteger(delta)) {
                throw new Error(`${where}: delta должен быть safe integer`);
            }
            if (reason !== undefined)
                assertNonEmptyString(reason, 'reason', where);
            const effect = draft.effects.find((candidate) => candidate.id === effectId);
            if (effect === undefined)
                throw new Error(`${where}: неизвестный эффект "${effectId}"`);
            const stored = effect.vars[key];
            const previous = stored === undefined ? 0 : stored;
            if (!Number.isSafeInteger(previous) || previous < 0) {
                throw new Error(`${where}: vars.${key} должен быть неотрицательным safe integer`);
            }
            const current = previous + delta;
            if (!Number.isSafeInteger(current) || current < 0) {
                throw new Error(`${where}: результат должен быть неотрицательным safe integer`);
            }
            if (current === previous)
                return;
            emit({
                type: 'EffectCounterChanged', effectId, defId: effect.defId,
                scope: structuredClone(effect.scope), key,
                previous: previous, current, delta,
                ...(reason !== undefined ? { reason } : {}),
            });
        },
        selectRandomUnit(candidateIds, sourceId) {
            const where = 'ops.selectRandomUnit';
            if (!Array.isArray(candidateIds))
                throw new Error(`${where}: candidateIds должен быть массивом`);
            if (sourceId !== undefined)
                assertNonEmptyString(sourceId, 'sourceId', where);
            const seen = new Set();
            const candidates = [];
            for (const candidateId of candidateIds) {
                assertNonEmptyString(candidateId, 'candidateIds[]', where);
                if (seen.has(candidateId))
                    continue;
                seen.add(candidateId);
                const inst = requireInstance(draft, candidateId, where);
                if (inst.zone !== 'board' || inst.cellId === undefined
                    || draft.board.cells[inst.cellId]?.occupantId !== inst.id) {
                    throw new Error(`${where}: кандидат "${candidateId}" не занимает ячейку поля`);
                }
                candidates.push({ instanceId: inst.id, cellId: inst.cellId, seq: inst.seq });
            }
            if (candidates.length === 0)
                return null;
            candidates.sort((a, b) => compareCellIds(a.cellId, b.cellId) || a.seq - b.seq);
            const [index, nextRng] = rngInt(draft.rng, 0, candidates.length - 1);
            draft.rng = nextRng;
            const selected = candidates[index];
            emit({
                type: 'RandomUnitSelectionResolved',
                ...(sourceId !== undefined ? { sourceId } : {}),
                candidates: candidates.map(({ instanceId, cellId }) => ({ instanceId, cellId })),
                selected: { instanceId: selected.instanceId, cellId: selected.cellId },
            });
            return selected.instanceId;
        },
        destroy(instanceId, options) {
            const where = 'ops.destroy';
            const inst = requireInstance(draft, instanceId, where);
            if (inst.zone !== 'board' || inst.cellId === undefined) {
                throw new Error(`${where}: уничтожить можно только юнита на поле (экземпляр "${instanceId}" в зоне '${inst.zone}')`);
            }
            // Чистые replaceDeath-хуки ДО смерти (R3/D16): 'prevent' ⇒ юнит остаётся;
            // Op[] ⇒ движок ИНТЕРПРЕТИРУЕТ дескрипторы ВМЕСТО смерти (перерождение) через
            // тот же путь ops → события. null/нет deps ⇒ Гибель продолжается.
            const replacement = deps.applyDeathReplacement ? deps.applyDeathReplacement(draft, instanceId) : null;
            if (replacement === 'prevent') {
                return; // смерть отменена — событий нет
            }
            if (replacement !== null) {
                if (replacementDestroyChain.has(instanceId)) {
                    throw new Error(`ops.destroy: цикл replaceDeath Op[] при повторном destroy("${instanceId}")`);
                }
                replacementDestroyChain.add(instanceId);
                try {
                    for (const op of replacement)
                        interpretOp(ops, op);
                }
                finally {
                    replacementDestroyChain.delete(instanceId);
                }
                return; // смерть заменена исполнением Op[] — UnitDied не эмитится
            }
            const cellId = inst.cellId;
            const isToken = inst.isToken === true;
            cleanupBoundToHost(inst.id);
            emit({
                type: 'UnitDied', instanceId, cellId, defId: inst.defId,
                ownerId: inst.ownerId, controllerIdAtDeath: inst.controllerId, isToken, cause: 'destroyed',
                ...(options?.sourceId !== undefined ? { sourceId: options.sourceId } : {}),
            });
            if (!isToken) {
                // Обычная карта уходит в сброс (D25); токен уже удалён reducer'ом.
                emit({ type: 'CardMoved', instanceId, from: 'board', to: 'discard' });
            }
        },
        moveCard(instanceId, toZone) {
            const where = 'ops.moveCard';
            const inst = requireInstance(draft, instanceId, where);
            if (deps.isCardMoveBlocked?.(instanceId) === true)
                return;
            if (toZone === 'board') {
                throw new Error(`${where}: перемещение в зону 'board' не поддерживается — на поле ставят вскрытие плана (M4) и createToken`);
            }
            if (toZone !== 'deck' && toZone !== 'hand' && toZone !== 'discard') {
                throw new Error(`${where}: неизвестная зона "${String(toZone)}" (ожидалась deck | hand | discard)`);
            }
            if (inst.zone === toZone) {
                throw new Error(`${where}: карта "${instanceId}" уже в зоне '${toZone}'`);
            }
            if (inst.zone === 'board')
                cleanupBoundToHost(inst.id);
            emit({ type: 'CardMoved', instanceId, from: inst.zone, to: toZone });
        },
        changeRule(key, value) {
            const where = 'ops.changeRule';
            assertNonEmptyString(key, 'key', where);
            const t = typeof value;
            if (t !== 'number' && t !== 'string' && t !== 'boolean') {
                throw new Error(`${where}: значение правила должно быть number | string | boolean (получено ${t})`);
            }
            if (typeof value === 'number') {
                assertFiniteNumber(value, where);
                emit({ type: 'RuleChanged', key, value: normalizeZero(value) });
                return;
            }
            emit({ type: 'RuleChanged', key, value });
        },
        setVar(target, key, value) {
            const where = 'ops.setVar';
            assertNonEmptyString(key, 'key', where);
            if (value === undefined) {
                throw new Error(`${where}: value не может быть undefined — нужно JSON-значение (D29/R16)`);
            }
            if (target.kind === 'unit') {
                requireInstance(draft, target.instanceId, where);
            }
            else if (target.kind === 'effect') {
                if (!draft.effects.some((e) => e.id === target.effectId)) {
                    throw new Error(`${where}: неизвестный эффект "${target.effectId}"`);
                }
            }
            else if (!draft.attachments.some((attachment) => attachment.attachmentId === target.attachmentId)) {
                throw new Error(`${where}: неизвестный attachment "${target.attachmentId}"`);
            }
            // Клон в событие: очередь/журнал не разделяют ссылку с вызывающим кодом.
            const stored = typeof value === 'number' ? normalizeZero(value) : structuredClone(value);
            emit({
                type: 'VarChanged', target: structuredClone(target), key, value: stored,
                ...(deps.isPublicVar?.(target, key) === true ? { public: true } : {}),
            });
        },
    };
    return ops;
}
//# sourceMappingURL=ops.js.map